'use client';

import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { isFunction } from 'shared/lib/utils';

import { cn } from 'components/lib/utils';

const EditableField = forwardRef(function EditableField(
  {
    className,
    isEditing,
    title,
    originalValue,
    fieldPlaceholder,
    onChange,
    onBlur,
    onFocus,
    element = 'input',
    textAreaRows = 1,
    markdown = false,
    markdownMobileTiny = false,
  },
  ref
) {
  const [fieldValue, setFieldValue] = useState(originalValue);

  if (!isEditing) {
    return (
      <div className={clsx(['break-nicely', className])} ref={ref}>
        {(() => {
          if (!markdown) {
            return originalValue;
          }

          return (
            <ReactMarkdown
              className={cn(
                'markdown',
                markdownMobileTiny && 'markdown-mobile-tiny'
              )}
              remarkPlugins={[remarkGfm]}
            >
              {originalValue}
            </ReactMarkdown>
          );
        })()}
      </div>
    );
  }

  if (element === 'input') {
    return (
      <input
        type="text"
        ref={ref}
        title={title}
        className={clsx(
          className,
          'w-full rounded px-1 hover:cursor-pointer hover:bg-gray-200 focus:cursor-text focus:bg-white'
        )}
        placeholder={fieldPlaceholder}
        value={fieldValue}
        onChange={(e) => {
          setFieldValue(e.target.value);

          if (isFunction(onChange)) {
            onChange(e, e.target.value);
          }
        }}
        onBlur={(e) => onBlur(e, fieldValue)}
        onFocus={(e) => isFunction(onFocus) && onFocus(e, fieldValue)}
      />
    );
  }

  if (element === 'textarea') {
    return (
      <TextareaAutosize
        className={clsx(
          className,
          'w-full overflow-y-hidden rounded px-1 hover:cursor-pointer hover:bg-gray-200 focus:cursor-text focus:bg-white'
        )}
        title={title}
        ref={ref}
        placeholder={fieldPlaceholder}
        minRows={textAreaRows}
        value={fieldValue || ''}
        onChange={(e) => {
          setFieldValue(e.target.value);

          if (isFunction(onChange)) {
            onChange(e, e.target.value);
          }
        }}
        onBlur={(e) => onBlur(e, fieldValue)}
        onFocus={(e) => isFunction(onFocus) && onFocus(e, fieldValue)}
      />
    );
  }

  console.error(`Don't know how to render a '${element}'`);

  return null;
});

export default EditableField;
